import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AdminDeckDto, AdminDeckListResponseDto, CardTemplateResponseDto, DailyCardTemplateDto, DailyCardTemplateListResponseDto, DeckBackListResponseDto, ThreeCardTextDto, ThreeCardTextListResponseDto } from '@lunary/api-client'
import { reloadDeck } from '@lunary/ui-web/deck'
import { api } from '../../shared/api/client'

export const DECKS_KEY = ['admin', 'decks'] as const
export const DECK_BACKS_KEY = ['admin', 'deck-backs'] as const
export const TEMPLATES_KEY = ['admin', 'daily-card-templates'] as const
export const THREE_TEXTS_KEY = ['admin', 'three-card-texts'] as const

export type DeckKind = AdminDeckDto['kind']

export type DeckPatch = { name?: string; description?: string; enabled?: boolean; is_default?: boolean; back_id?: string }

export function useDecks() {
  return useQuery({
    queryKey: DECKS_KEY,
    queryFn: () => api.get<AdminDeckListResponseDto>('/admin/decks'),
    retry: false,
  })
}

export function useDeckBacks() {
  return useQuery({
    queryKey: DECK_BACKS_KEY,
    queryFn: () => api.get<DeckBackListResponseDto>('/admin/deck-backs'),
    retry: false,
  })
}

function useDeckSync() {
  const qc = useQueryClient()
  return (deck: AdminDeckDto) => {
    qc.setQueryData<AdminDeckListResponseDto>(DECKS_KEY, (old) =>
      old ? { ...old, items: old.items.map((d) => (d.id === deck.id ? deck : d)) } : old,
    )
    void qc.invalidateQueries({ queryKey: DECK_BACKS_KEY })
    reloadDeck()
  }
}

export function useCreateDeck() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { name: string; kind: DeckKind }) => api.post<AdminDeckDto>('/admin/decks', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: DECKS_KEY }),
  })
}

export function useUpdateDeck(id: string) {
  const sync = useDeckSync()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: DeckPatch) => api.patch<AdminDeckDto>(`/admin/decks/${id}`, patch),
    onSuccess: (deck, patch) => {
      sync(deck)
      if (patch.is_default === true) void qc.invalidateQueries({ queryKey: DECKS_KEY })
    },
  })
}

export function useDeleteDeck(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete<void>(`/admin/decks/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DECKS_KEY })
      reloadDeck()
    },
  })
}

export function useUploadCard(id: string) {
  const sync = useDeckSync()
  return useMutation({
    mutationFn: (input: { cardId: string; file: Blob }) =>
      api.putBlob<AdminDeckDto>(`/admin/decks/${id}/cards/${encodeURIComponent(input.cardId)}`, input.file),
    onSuccess: sync,
  })
}

export function useRemoveCard(id: string) {
  const sync = useDeckSync()
  return useMutation({
    mutationFn: (cardId: string) => api.delete<AdminDeckDto>(`/admin/decks/${id}/cards/${encodeURIComponent(cardId)}`),
    onSuccess: sync,
  })
}

export function useUploadBack(id: string) {
  const sync = useDeckSync()
  return useMutation({
    mutationFn: (file: Blob) => api.putBlob<AdminDeckDto>(`/admin/decks/${id}/back`, file),
    onSuccess: sync,
  })
}

export function useDeleteBack() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (backId: string) => api.delete<void>(`/admin/deck-backs/${encodeURIComponent(backId)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: DECK_BACKS_KEY })
      void qc.invalidateQueries({ queryKey: DECKS_KEY })
      reloadDeck()
    },
  })
}

export function useAddCustomCard(deckId: string) {
  const sync = useDeckSync()
  return useMutation({
    mutationFn: (input: CustomCardPatch & { name: string }) => api.post<AdminDeckDto>(`/admin/decks/${deckId}/custom-cards`, input),
    onSuccess: sync,
  })
}

export type CustomCardPatch = Partial<CardTemplateResponseDto> & { name?: string; archived?: boolean }

export function useUpdateCustomCard(deckId: string) {
  const sync = useDeckSync()
  return useMutation({
    mutationFn: (input: { id: string; patch: CustomCardPatch }) => api.patch<AdminDeckDto>(`/admin/decks/${deckId}/custom-cards/${input.id}`, input.patch),
    onSuccess: sync,
  })
}

export function useUploadCustomImage(deckId: string) {
  const sync = useDeckSync()
  return useMutation({
    mutationFn: (input: { id: string; file: Blob }) => api.putBlob<AdminDeckDto>(`/admin/decks/${deckId}/custom-cards/${input.id}/image`, input.file),
    onSuccess: sync,
  })
}

export function useTemplates() {
  return useQuery({
    queryKey: TEMPLATES_KEY,
    queryFn: () => api.get<DailyCardTemplateListResponseDto>('/admin/daily-card-templates'),
    retry: false,
  })
}

export function useSaveTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { number: number; text: CardTemplateResponseDto }) =>
      api.put<DailyCardTemplateDto>(`/admin/daily-card-templates/${input.number}`, input.text),
    onSuccess: () => void qc.invalidateQueries({ queryKey: TEMPLATES_KEY }),
  })
}

export function useThreeCardTexts() {
  return useQuery({
    queryKey: THREE_TEXTS_KEY,
    queryFn: () => api.get<ThreeCardTextListResponseDto>('/admin/three-card-texts'),
    retry: false,
  })
}

export function useSaveThreeCardText() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { cardId: string; body: string; hint: string; advice: string }) =>
      api.put<ThreeCardTextDto>(`/admin/three-card-texts/${encodeURIComponent(input.cardId)}`, {
        body: input.body,
        hint: input.hint,
        advice: input.advice,
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: THREE_TEXTS_KEY }),
  })
}

export function useResetThreeCardText() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (cardId: string) => api.post<ThreeCardTextDto>(`/admin/three-card-texts/${encodeURIComponent(cardId)}/reset`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: THREE_TEXTS_KEY }),
  })
}
